/**
 * useWebSocket Hook - Phase 5: Real-time Collaboration
 * Manages WebSocket connection lifecycle and provides connection state
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '@/store/auth';
import toast from 'react-hot-toast';

export interface UseWebSocketReturn {
  socket: Socket | null;
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  connect: () => void;
  disconnect: () => void;
  emit: (event: string, data: any) => void;
  joinRoom: (roomId: string) => void;
  leaveRoom: (roomId: string) => void;
}

export function useWebSocket(): UseWebSocketReturn {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  const { user, isAuthenticated } = useAuthStore();

  // Check if WebSocket is disabled via environment variable
  const isWebSocketDisabled = import.meta.env.VITE_DISABLE_WEBSOCKET === 'true';

  const connect = useCallback(() => {
    // If WebSocket is disabled, don't attempt connection
    if (isWebSocketDisabled) {
      return;
    }

    if (socket?.connected) {
      return;
    }

    setIsConnecting(true);
    setError(null);

    // Determine WebSocket URL
    // In production, always use Railway backend (never use api.synapmarketing.com which doesn't exist)
    // In development, use localhost or environment variable
    let wsUrl: string;

    if (import.meta.env.PROD) {
      // Production: always use Railway backend
      wsUrl = 'https://marketing-ai-platform-production.up.railway.app';
    } else {
      // Development: use environment variable, localhost, or window origin
      wsUrl = import.meta.env.VITE_WS_URL ||
              (typeof window !== 'undefined' && window.location.origin.includes('localhost')
                ? 'http://localhost:4000'
                : window.location.origin) ||
              'http://localhost:4000';
    }

    // Prepare auth data - use user data if available, otherwise let backend handle anonymous connection
    const authData = isAuthenticated && user ? {
      userId: user.id,
      userEmail: user.email,
    } : undefined;

    const newSocket = io(wsUrl, {
      ...(authData && { auth: authData }),
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: maxReconnectAttempts,
    });

    // Connection successful
    newSocket.on('connect', () => {
      setIsConnected(true);
      setIsConnecting(false);
      setError(null);
      reconnectAttempts.current = 0;
      toast.success('Real-time connection established');
    });

    // Connection confirmation from server
    newSocket.on('connected', (_data: { socketId: string; userId: string; timestamp: string }) => {
      // Server confirmed connection
    });

    // Disconnection
    newSocket.on('disconnect', (reason: string) => {
      setIsConnected(false);
      setIsConnecting(false);

      // Show user-friendly message based on reason
      if (reason === 'io server disconnect') {
        setError('Server disconnected the connection');
        toast.error('Connection closed by server');
      } else if (reason === 'transport close' || reason === 'ping timeout') {
        setError('Connection lost');
        toast.error('Connection lost. Attempting to reconnect...');
      }
    });

    // Reconnection attempt
    newSocket.io.on('reconnect_attempt', (attempt: number) => {
      setIsConnecting(true);
      reconnectAttempts.current = attempt;
    });

    // Reconnection successful
    newSocket.io.on('reconnect', (attempt: number) => {
      setIsConnected(true);
      setIsConnecting(false);
      setError(null);
      toast.success('Reconnected successfully');
    });

    // Reconnection failed
    newSocket.io.on('reconnect_failed', () => {
      console.error('❌ Reconnection failed after maximum attempts');
      setIsConnecting(false);
      setError('Failed to reconnect after multiple attempts');
      toast.error('Could not reconnect. Please refresh the page.');
    });

    // Connection error
    newSocket.on('connect_error', (err: Error) => {
      // Always log for debugging
      console.error('❌ WebSocket connection error:', err.message);
      setIsConnecting(false);
      setError(err.message);

      // Only show error toast on first attempt
      if (reconnectAttempts.current === 0) {
        // Don't show toast in production to avoid annoying users
        if (import.meta.env.DEV) {
          toast.error(`Connection error: ${err.message}`);
        }
      }
    });

    // Server heartbeat
    newSocket.on('heartbeat', (_data: { timestamp: string; connectedClients: number }) => {
      // Heartbeat received
    });

    // Ping-pong for custom heartbeat
    newSocket.on('pong', (_data: { timestamp: string }) => {
      // Pong received
    });

    // Generic error handler
    newSocket.on('error', (err: Error) => {
      console.error('❌ WebSocket error:', err);
      setError(err.message);
    });

    setSocket(newSocket);
  }, [user, isAuthenticated, isWebSocketDisabled]);

  const disconnect = useCallback(() => {
    if (socket) {
      socket.disconnect();
      setSocket(null);
      setIsConnected(false);
      setIsConnecting(false);
      setError(null);
    }
  }, [socket]);

  const emit = useCallback(
    (event: string, data: any) => {
      if (isWebSocketDisabled) {
        return;
      }
      if (socket?.connected) {
        socket.emit(event, data);
      } else {
        console.warn(`⚠️ Cannot emit "${event}": Socket not connected`);
      }
    },
    [socket, isWebSocketDisabled]
  );

  const joinRoom = useCallback(
    (roomId: string) => {
      if (isWebSocketDisabled) {
        return;
      }
      if (socket?.connected) {
        socket.emit('join:room', roomId);
      } else {
        console.warn(`⚠️ Cannot join room "${roomId}": Socket not connected`);
      }
    },
    [socket, isWebSocketDisabled]
  );

  const leaveRoom = useCallback(
    (roomId: string) => {
      if (isWebSocketDisabled) {
        return;
      }
      if (socket?.connected) {
        socket.emit('leave:room', roomId);
      } else {
        console.warn(`⚠️ Cannot leave room "${roomId}": Socket not connected`);
      }
    },
    [socket, isWebSocketDisabled]
  );

  // Auto-connect on mount (with optional user auth data)
  useEffect(() => {
    if (!socket && !isWebSocketDisabled) {
      connect();
    }

    // Cleanup on unmount or when user logs out
    return () => {
      if (socket) {
        disconnect();
      }
    };
  }, []); // Only run on mount

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (socket) {
        socket.removeAllListeners();
        socket.disconnect();
      }
    };
  }, []);

  return {
    socket,
    isConnected,
    isConnecting,
    error,
    connect,
    disconnect,
    emit,
    joinRoom,
    leaveRoom,
  };
}
